import { alpha, darken, lighten, Theme } from '@mui/material/styles';

/**
 * Colour tokens for the marketing site, derived from the selected app theme
 * (Fidava Dark by default — see ColorModeContext). Sections use these instead of
 * hex literals so every theme in the picker recolours the whole page, the
 * same way the app's screens follow the theme.
 *
 * The old hard-coded palette maps onto them like this:
 *   cobalt #2340B0 / #6F8BFF, indigo #6366f1, rgba(35,64,176,a),
 *   rgba(99,102,241,a), rgba(111,139,255,a)          → accent / accentA(a)
 *   navy tints rgba(17,45,92,a), rgba(30,41,75,a), … → accentDeepA(a)
 *   dark surfaces #0b1220, #0d1420, #111a2f, #18181d → page / surface / surfaceDeep
 *   warm neutrals #edece7, #f4f3ef, #f8f7f4, …       → page / surface / surfaceSoft
 *   #d6d3cb, #e2dfd8 (hairlines)                      → border
 *   #0f172a, #5d5c66, #4b4a53 (light-mode ink)        → ink / inkSoft
 *   #f1f5f9, rgba(226,232,240,a) (dark-mode ink)      → ink / inkA(a)
 */
export const siteTokens = (theme: Theme) => {
  const dark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const accent2 = theme.palette.secondary.main;
  const page = theme.palette.background.default;
  const surface = theme.palette.background.paper;
  const ink = theme.palette.text.primary;

  return {
    dark,
    /** Brand accent of the theme (links, highlights, icons, glows). */
    accent,
    /** Second accent — the other end of gradients. */
    accent2,
    /** Text/icons placed on a filled accent surface. */
    onAccent: theme.palette.primary.contrastText,
    accentA: (a: number) => alpha(accent, a),
    accent2A: (a: number) => alpha(accent2, a),
    /** Deep accent tint for shadows / dark gradients (was navy). */
    accentDeepA: (a: number) => alpha(dark ? darken(accent, 0.6) : darken(accent, 0.35), a),
    /** Accent → accent2 gradient. */
    gradient: (angle = 135) => `linear-gradient(${angle}deg, ${accent}, ${accent2})`,

    /** Page background. */
    page,
    /** Card / panel surface. */
    surface,
    /** Slightly recessed surface (inner wells, alternating bands). */
    surfaceSoft: dark ? lighten(page, 0.04) : darken(page, 0.03),
    /** Deepest surface (hero / showcase panels that were dark navy). */
    surfaceDeep: dark ? darken(page, 0.25) : darken(page, 0.06),
    pageA: (a: number) => alpha(page, a),
    surfaceA: (a: number) => alpha(surface, a),

    /** Body text. */
    ink,
    /** Secondary text. */
    inkSoft: theme.palette.text.secondary,
    inkA: (a: number) => alpha(ink, a),

    /** Hairlines and card borders. */
    border: theme.palette.divider,
    borderStrong: alpha(ink, dark ? 0.24 : 0.18),

    /** Card drop shadow tuned to the mode. */
    shadow: (a = 1) => dark
      ? `0 18px 40px ${alpha('#000', 0.45 * a)}`
      : `0 18px 40px ${alpha(darken(accent, 0.5), 0.12 * a)}`,
  };
};

export type SiteTokens = ReturnType<typeof siteTokens>;
